import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ProductCarouselRow } from "@/components/ProductCarouselRow";
import { HeroSection } from "@/components/HeroSection";
import { FeatureStrip } from "@/components/FeatureStrip";
import { BrandStatement } from "@/components/BrandStatement";
import { CategoryTiles } from "@/components/CategoryTiles";

import { AboutSection } from "@/components/AboutSection";
import { UnfailingGoods } from "@/components/UnfailingGoods";
import { SportingLifeSection } from "@/components/SportingLifeSection";
import { BuiltForSection } from "@/components/BuiltForSection";
import { LifestyleGallery } from "@/components/LifestyleGallery";

import { fetchProductsByCollection } from "@/lib/shopify";

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Premium Lifestyle Headwear | Coyote Creek Hat Co." },
      { name: "description", content: "Shop premium trucker hats, camo hats and Texas sporting headwear built for hunting and the outdoors. Wear something you stand for." },
      { property: "og:title", content: "Premium Lifestyle Headwear | Coyote Creek Hat Co." },
      { property: "og:description", content: "Shop premium trucker hats, camo hats and Texas sporting headwear built for hunting and the outdoors. Wear something you stand for." },
      { property: "og:url", content: "https://coyotecreekhatco.com/" },
      { property: "og:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SzMe6IFZhrZ7NgTJI79ZOppiF9b2/social-images/social-1779766489683-2026_Website_Logo.webp" },
      { name: "twitter:title", content: "Premium Lifestyle Headwear | Coyote Creek Hat Co." },
      { name: "twitter:description", content: "Shop premium trucker hats, camo hats and Texas sporting headwear built for hunting and the outdoors. Wear something you stand for." },
      { name: "twitter:image", content: "https://storage.googleapis.com/gpt-engineer-file-uploads/SzMe6IFZhrZ7NgTJI79ZOppiF9b2/social-images/social-1779766489683-2026_Website_Logo.webp" },
    ],
    links: [
      { rel: "canonical", href: "https://coyotecreekhatco.com/" },
      {
        rel: "preload",
        as: "image",
        href: "/__l5e/assets-v1/d7fbe24c-b5b6-4177-9f76-6962c6e61a04/hero-sporting-life.png",
        fetchPriority: "high",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Coyote Creek Hat Co.",
          url: "https://coyotecreekhatco.com",
          logo: "https://storage.googleapis.com/gpt-engineer-file-uploads/SzMe6IFZhrZ7NgTJI79ZOppiF9b2/social-images/social-1779766489683-2026_Website_Logo.webp",
          description: "Premium lifestyle headwear and apparel from the heart of Texas.",
          sameAs: [
            "https://www.instagram.com/coyotecreekhatco/",
            "https://www.facebook.com/coyotecreekhatco/",
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          name: "Coyote Creek Hat Co.",
          url: "https://coyotecreekhatco.com",
        }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  const { data: products, isLoading } = useQuery({
    queryKey: ["products", "collection", "new-gear"],
    queryFn: () => fetchProductsByCollection("new-gear", 50),
  });
  const [activeTab, setActiveTab] = useState<"hats" | "tops">("hats");
  const activeHandle = activeTab === "hats" ? "all-hats" : "tops";
  const shopAll = useQuery({
    queryKey: ["products", "collection", activeHandle],
    queryFn: () => fetchProductsByCollection(activeHandle, 50),
  });



  return (
    <>
      <HeroSection />
      <FeatureStrip />


      {/* New Gear */}
      <section className="bg-background pt-20">
        <ProductCarouselRow
          heading="NEW GEAR"
          subheading="Premium Sporting Headwear"
          products={products}
          isLoading={isLoading}
          shopAllLink={{ label: "Shop All New Gear", to: "/collections/$collection", params: { collection: "new-gear" } }}
        />
      </section>

      {/* Shop All */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-[1400px] px-6">
          <div className="border-t border-[#D4CCBC]/60 pt-20" />
        </div>
        <ProductCarouselRow
          heading="SHOP ALL"
          subheading="Get In It, Live It"
          products={shopAll.data}
          isLoading={shopAll.isLoading}
          scrollResetKey={activeTab}
           shopAllLink={
             activeTab === "hats"
               ? { label: "Shop all hats", to: "/collections/$collection", params: { collection: "all-hats" } }
               : { label: "Shop all tops", to: "/collections/$collection", params: { collection: "tops" } }
           }
        >
          <div className="mt-6 flex gap-8">
            {(["hats", "tops"] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`pb-1 font-ui text-xs uppercase tracking-[2px] transition-colors ${
                  activeTab === tab
                    ? "border-b-2 border-[#2A1510] text-[#2A1510]"
                    : "text-[#9C9287] hover:text-[#2A1510]"
                }`}
              >
                {tab === "hats" ? "Hats" : "Tops"}
              </button>
            ))}
          </div>
        </ProductCarouselRow>
      </section>

      <BrandStatement />

      <AboutSection />
      <SportingLifeSection />
      <UnfailingGoods />
      <CategoryTiles />
      <BuiltForSection />
      <LifestyleGallery />
    </>
  );
}
